import React, { useState, useEffect } from 'react';
import BackgroundShapes from '../common/BackgroundShapes';
import { previewAPI } from '../../services/apiService';
import { getPageUrl } from '../../utils/urlHelpers';

interface PreviewPageProps {
  onNavigate: (page: string) => void;
}

interface PreviewProfile {
  id: string;
  name: string;
  age: number;
  occupation: string;
  location: string[];
  budget: string;
  bio: string;
  preferences: {
    cleanliness: string;
    socialLevel: string;
    workSchedule: string;
  };
  compatibility: number;
  photo?: string;
}

const PreviewPage: React.FC<PreviewPageProps> = ({ onNavigate }) => {
  // URL configuration for this page - environment-aware
  // This makes the component aware of its URL configuration for potential future use (sharing, analytics, etc.)
  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  const previewUrl = getPageUrl('/preview');
  
  const [profiles, setProfiles] = useState<PreviewProfile[]>([]);
  const [stats, setStats] = useState({ totalUsers: 0, successRate: 0, avgMatchTime: 0 });
  const [loading, setLoading] = useState(true);
  const [currentProfileIndex, setCurrentProfileIndex] = useState(0);

  useEffect(() => {
    const loadData = async () => {
      try {
        const [profilesData, statsData] = await Promise.all([
          previewAPI.getProfiles(),
          previewAPI.getStats()
        ]);
        setProfiles(profilesData.data?.profiles || mockProfiles);
        setStats(statsData.data?.stats || mockStats);
      } catch (error) {
        // Fallback to mock data if API fails
        console.log('Using mock data for preview');
        setProfiles(mockProfiles);
        setStats(mockStats);
      } finally {
        setLoading(false);
      }
    };

    loadData();
  }, []);

  const nextProfile = () => {
    setCurrentProfileIndex((prev) => (prev + 1) % profiles.length);
  };

  const prevProfile = () => {
    setCurrentProfileIndex((prev) => (prev - 1 + profiles.length) % profiles.length);
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-paper flex items-center justify-center">
        <div className="text-coffee text-xl">Loading preview...</div>
      </div>
    );
  }

  const currentProfile = profiles[currentProfileIndex];

  return (
    <div className="min-h-screen bg-paper relative">
      <BackgroundShapes />
      
      <div className="container mx-auto px-4 py-8 relative z-10">
        {/* Header */}
        <div className="text-center mb-8">
          <h1 className="text-4xl font-bold text-coffee mb-2">See What You Get</h1>
          <p className="text-coffee/70 mb-8">Browse sample roommate matches • No signup required</p>
          
          {/* Stats */}
          <div className="flex justify-center items-center gap-8 mb-8">
            <div className="bg-white border border-coffee rounded-lg p-2">
              <div className="text-2xl font-bold text-coffee">{stats.totalUsers.toLocaleString()}</div>
              <div className="text-sm text-coffee">Active Users</div>
            </div>
            <div className="bg-white border border-coffee rounded-lg p-2">
              <div className="text-2xl font-bold text-coffee">{stats.successRate}%</div>
              <div className="text-sm text-coffee">Success Rate</div>
            </div>
            <div className="bg-white border border-coffee rounded-lg p-2">
              <div className="text-2xl font-bold text-coffee">{stats.avgMatchTime} days</div>
              <div className="text-sm text-coffee">Avg. Match Time</div>
            </div>
          </div>
        </div>

        {/* Profile Card */}
        <div className="max-w-md mx-auto">
          <div className="bg-cream rounded-[32px] border-4 border-coffee shadow-[8px_8px_0px_#594A42] p-8 relative mb-8">
            {/* Match Badge */}
            <div className="absolute top-6 right-6 bg-terracotta text-white px-4 py-1 rounded-full font-bold border border-coffee shadow-sm z-10">
              {currentProfile.compatibility}% Match
            </div>

            {/* Profile Header */}
            <div className="mb-6">
              <h2 className="text-2xl font-bold text-coffee">{currentProfile.name}, {currentProfile.age}</h2>
              <p className="text-coffee/70">{currentProfile.occupation}</p>
            </div>

            {/* Profile Photo Placeholder */}
            <div className="w-24 h-24 rounded-full bg-steel text-white border-4 border-white ring-2 ring-coffee flex items-center justify-center text-3xl font-bold shadow-md mx-auto mb-4">
                {currentProfile.name.charAt(0)}
            </div>

            {/* Location & Budget */}
            <div className="grid grid-cols-2 gap-4 mb-6">
              <div>
                <h3 className="text-coffee font-semibold mb-2">Preferred Areas</h3>
                <div className="flex flex-wrap gap-2">
                  {currentProfile.location.map((loc, index) => (
                    <span key={index} className="bg-transparent border-2 border-coffee text-coffee px-3 py-1 rounded-full text-sm">
                      {loc}
                    </span>
                  ))}
                </div>
              </div>
              <div>
                <h3 className="text-coffee font-semibold mb-2">Budget</h3>
                <p className="text-coffee">{currentProfile.budget}</p>
              </div>
            </div>

            {/* Bio */}
            <div className="mb-6">
              <h3 className="text-coffee font-semibold mb-2">About</h3>
              <p className="text-coffee/80">{currentProfile.bio}</p>
            </div>

            {/* Lifestyle Preferences */}
            <div className="grid grid-cols-3 gap-4 mb-6">
              <div>
                <h4 className="text-coffee font-semibold mb-1">Cleanliness</h4>
                <p className="text-coffee/70 text-sm">{currentProfile.preferences.cleanliness}</p>
              </div>
              <div>
                <h4 className="text-coffee font-semibold mb-1">Social Level</h4>
                <p className="text-coffee/70 text-sm">{currentProfile.preferences.socialLevel}</p>
              </div>
              <div>
                <h4 className="text-coffee font-semibold mb-1">Work Schedule</h4>
                <p className="text-coffee/70 text-sm">{currentProfile.preferences.workSchedule}</p>
              </div>
            </div>

            {/* Navigation */}
            <div className="flex justify-between items-center">
              <button
                onClick={prevProfile}
                className="px-4 py-2 rounded-lg border-2 border-coffee text-coffee font-bold hover:bg-coffee/5 transition-colors disabled:opacity-30"
                disabled={profiles.length <= 1}
              >
                ← Previous
              </button>
              
              <span className="text-coffee/70">
                {currentProfileIndex + 1} of {profiles.length}
              </span>
              
              <button
                onClick={nextProfile}
                className="px-4 py-2 rounded-lg border-2 border-coffee text-coffee font-bold hover:bg-coffee/5 transition-colors disabled:opacity-30"
                disabled={profiles.length <= 1}
              >
                Next →
              </button>
            </div>
          </div>

          {/* Call to Action */}
          <div className="text-center">
            <p className="text-coffee/70 mb-6">Ready to find your perfect roommate match?</p>
            <div className="flex flex-col md:flex-row gap-4 justify-center">
              <button
                onClick={() => onNavigate('signup')}
                className="bg-terracotta text-white border-2 border-coffee px-8 py-3 rounded-xl font-bold shadow-[4px_4px_0px_#594A42] hover:scale-105 transition-transform"
              >
                🚀 Get Started - It's Free
              </button>
              <button
                onClick={() => onNavigate('landing')}
                className="bg-steel text-white border-2 border-coffee px-8 py-3 rounded-xl font-bold shadow-[4px_4px_0px_#594A42] hover:scale-105 transition-transform"
              >
                ← Back to Home
              </button>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

// Mock data fallbacks
const mockProfiles: PreviewProfile[] = [
  {
    id: '1',
    name: 'Sarah Chen',
    age: 26,
    occupation: 'Software Engineer',
    location: ['Manhattan - East Village', 'Brooklyn - Williamsburg'],
    budget: '$1,800 - $2,200/month',
    bio: "I'm a tech professional who loves exploring NYC's food scene and hiking on weekends. Looking for someone clean, respectful, and social but not party-crazy. I work remotely 2-3 days a week.",
    preferences: {
      cleanliness: 'Very Clean',
      socialLevel: 'Social but not loud',
      workSchedule: 'Hybrid remote'
    },
    compatibility: 94
  },
  {
    id: '2',
    name: 'Marcus Johnson',
    age: 28,
    occupation: 'Marketing Manager',
    location: ['Manhattan - Upper West Side', 'Jersey City - Downtown'],
    budget: '$1,600 - $2,000/month',
    bio: "Fitness enthusiast who loves cooking and hosting small gatherings. I'm pretty social and enjoy having friends over occasionally. Work standard hours and travel sometimes for work.",
    preferences: {
      cleanliness: 'Clean & Organized',
      socialLevel: 'Love hosting friends',
      workSchedule: 'Standard 9-5'
    },
    compatibility: 87
  },
  {
    id: '3',
    name: 'Emma Rodriguez',
    age: 24,
    occupation: 'Graphic Designer',
    location: ['Brooklyn - Park Slope', 'Queens - Astoria'],
    budget: '$1,400 - $1,800/month',
    bio: "Creative professional who's pretty chill and low-key. I love plants, reading, and quiet weekends. Looking for someone who respects personal space but is friendly.",
    preferences: {
      cleanliness: 'Moderately clean',
      socialLevel: 'Quiet & respectful',
      workSchedule: 'Creative schedule'
    },
    compatibility: 91
  }
];

const mockStats = {
  totalUsers: 10847,
  successRate: 94,
  avgMatchTime: 2.3
};

export default PreviewPage;